import { createClient } from "@/app/utils/supabase/client";
import {
  Supplier,
  SupplierInsert,
  SupplierUpdate,
  SupplierAddress,
  SupplierAddressInsert,
  SupplierAddressUpdate,
  SupplierContact,
  SupplierContactInsert,
  SupplierContactUpdate
} from "@/app/lib/supabase/types";
import { SUPABASE_TABLES } from "@/app/lib/supabase/supabaseDBConstants";

export const suppliersApi = {
  // Supplier operations
  async getSuppliers(companyId: string): Promise<Supplier[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIERS)
      .select("*")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("name", { ascending: true });

    if (error) {
      console.error("Error fetching suppliers:", error);
      throw error;
    }

    return data || [];
  },

  async getSupplier(supplierId: string): Promise<Supplier> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIERS)
      .select("*")
      .eq("id", supplierId)
      .is("deleted_at", null)
      .single();

    if (error) {
      console.error("Error fetching supplier:", error);
      throw error;
    }

    if (!data) {
      throw new Error("Supplier not found");
    }

    return data;
  },

  async createSupplier(supplier: SupplierInsert): Promise<Supplier> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIERS)
      .insert(supplier)
      .select()
      .single();

    if (error) {
      console.error("Error creating supplier:", error);
      throw error;
    }

    return data;
  },

  async updateSupplier(supplierId: string, updates: SupplierUpdate): Promise<Supplier> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIERS)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", supplierId)
      .select()
      .single();

    if (error) {
      console.error("Error updating supplier:", error);
      throw error;
    }

    return data;
  },

  async deleteSupplier(supplierId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIERS)
      .update({
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", supplierId);

    if (error) {
      console.error("Error deleting supplier:", error);
      throw error;
    }
  },

  // Supplier address operations
  async getSupplierAddresses(supplierId: string): Promise<SupplierAddress[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIER_ADDRESSES)
      .select("*")
      .eq("supplier_id", supplierId)
      .is("deleted_at", null)
      .order("is_main", { ascending: false })
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching supplier addresses:", error);
      throw error;
    }

    return data || [];
  },

  async getSupplierAddress(addressId: string): Promise<SupplierAddress> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIER_ADDRESSES)
      .select("*")
      .eq("id", addressId)
      .is("deleted_at", null)
      .single();

    if (error) {
      console.error("Error fetching supplier address:", error);
      throw error;
    }

    if (!data) {
      throw new Error("Supplier address not found");
    }

    return data;
  },

  async createSupplierAddress(address: SupplierAddressInsert): Promise<SupplierAddress> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIER_ADDRESSES)
      .insert(address)
      .select()
      .single();

    if (error) {
      console.error("Error creating supplier address:", error);
      throw error;
    }

    return data;
  },

  async updateSupplierAddress(addressId: string, updates: SupplierAddressUpdate): Promise<SupplierAddress> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIER_ADDRESSES)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", addressId)
      .select()
      .single();

    if (error) {
      console.error("Error updating supplier address:", error);
      throw error;
    }

    return data;
  },

  async deleteSupplierAddress(addressId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIER_ADDRESSES)
      .update({
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", addressId);

    if (error) {
      console.error("Error deleting supplier address:", error);
      throw error;
    }
  },

  // Supplier contact operations
  async getSupplierContacts(supplierId: string): Promise<SupplierContact[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIER_CONTACTS)
      .select("*")
      .eq("supplier_id", supplierId)
      .order("is_main", { ascending: false })
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching supplier contacts:", error);
      throw error;
    }

    return data || [];
  },

  async getSupplierContact(contactId: string): Promise<SupplierContact> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIER_CONTACTS)
      .select("*")
      .eq("id", contactId)
      .single();

    if (error) {
      console.error("Error fetching supplier contact:", error);
      throw error;
    }

    if (!data) {
      throw new Error("Supplier contact not found");
    }

    return data;
  },

  async createSupplierContact(contact: SupplierContactInsert): Promise<SupplierContact> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIER_CONTACTS)
      .insert(contact)
      .select()
      .single();

    if (error) {
      console.error("Error creating supplier contact:", error);
      throw error;
    }

    return data;
  },

  async updateSupplierContact(contactId: string, updates: SupplierContactUpdate): Promise<SupplierContact> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIER_CONTACTS)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", contactId)
      .select()
      .single();

    if (error) {
      console.error("Error updating supplier contact:", error);
      throw error;
    }

    return data;
  },

  async deleteSupplierContact(contactId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIER_CONTACTS)
      .delete()
      .eq("id", contactId);

    if (error) {
      console.error("Error deleting supplier contact:", error);
      throw error;
    }
  },

  // Get contacts for a specific supplier address
  async getSupplierAddressContacts(addressId: string): Promise<SupplierContact[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SUPPLIER_CONTACTS)
      .select("*")
      .eq("supplier_address_id", addressId)
      .order("is_main", { ascending: false })
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching supplier address contacts:", error);
      throw error;
    }

    return data || [];
  },
};